import { useEffect, useRef, useState } from "react";
import {
  Checkbox,
  ListItemText,
  Typography,
  ListItem,
  ListItemButton,
  ListItemIcon,
  Box,
  Skeleton,
  TextField,
  InputAdornment,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import { FixedSizeList } from "react-window";
import { useAuth } from "../../context/AuthProvider";
import styled from "@emotion/styled";
import { useGetQuery } from "../../hooks/useQuery";
import { urlSuParametrais } from "../../functions/urlSuParametrais";

const KarvesContainer = styled(Box)`
  display: flex;
  flex-direction: row;
  gap: 4px;
  align-items: center;
  margin-bottom: 4px;
`;

const ListContainerWrapper = styled(Box)`
  width: 100%;
  height: calc(100% - 40px) !important; /* Account for the search field above */
  min-height: 250px; /* Fallback minimum height */
  display: flex;
  flex-direction: column;
  position: relative;
`;

const KarvesProduktyvumui = ({
  isLoading,
  savId,
  bandos,
  karves,
  changeKarves,
  handleZymVisasKarves,
  papildomaInfo,
  veisles,
  laktacijos,
  dynamicSize,
  stickyHeader,
  setOriginalusKarviuSkaicius = null,
}) => {
  const [zymVisos, setZymVisos] = useState(true);
  const [searchText, setSearchText] = useState("");
  const [hasInitialized, setHasInitialized] = useState(false);
  const auth = useAuth();
  const arSavininkas = auth.user.role === "Ūkis";
  const [listDimensions, setListDimensions] = useState({ width: 230, height: 250 });
  const containerRef = useRef(null);

  const papildomaInfoQueryResult = useGetQuery(
    papildomaInfo
      ? arSavininkas
        ? urlSuParametrais(`karves`, [
            { parameter: papildomaInfo, condition: "add", append: "papildoma-info", value: 1 },
          ])
        : urlSuParametrais(
            `karves`,
            [{ parameter: papildomaInfo, condition: "add", append: "papildoma-info", value: 1 }],
            savId
          )
      : null
  );

  const karviuList = papildomaInfoQueryResult || [];

  useEffect(() => {
    if (karviuList.length > 0 && !hasInitialized) {
      let selectedKarveNumbers = [];
      if (localStorage.getItem("buvesPuslapis") === "/") {
        const selectedKarves = localStorage.getItem("selectedKarves");

        if (selectedKarves) {
          try {
            const selectedCowsList = JSON.parse(selectedKarves);

            if (selectedCowsList && selectedCowsList.length > 0) {
              selectedKarveNumbers = selectedCowsList
                .map((k) => k.karNr)
                .filter((karNr) => karviuList.some((cow) => cow.karNr === karNr));

              if (selectedKarveNumbers.length > 0) {
                handleZymVisasKarves(selectedKarveNumbers);

                if (selectedKarveNumbers.length === karviuList.length) {
                  setZymVisos(true);
                } else {
                  setZymVisos(false);
                }
              }
            }
          } catch (error) {
            console.error("Failed to parse homeFilters from localStorage:", error);
          }
        }
      }

      setHasInitialized(true);
    }
  }, [karviuList, hasInitialized, handleZymVisasKarves]);

  useEffect(() => {
    if (hasInitialized && karviuList.length > 0) {
      if (karves.length > 0) {
        const selectedKarveObjects = karviuList.filter((cow) => karves.includes(cow.karNr));
        localStorage.setItem("selectedKarves", JSON.stringify(selectedKarveObjects));
      } else {
        localStorage.setItem("selectedKarves", JSON.stringify([]));
      }
    }
  }, [karves, karviuList, hasInitialized]);

  let banduKarves = karviuList;

  if (bandos && bandos.length > 0) {
    banduKarves = banduKarves.filter((k) => bandos.includes(k.banda));
  }

  if (veisles && veisles.length > 0) {
    banduKarves = banduKarves.filter((k) => veisles.includes(k.veisle));
  }

  if (laktacijos && laktacijos.length > 0) {
    banduKarves = banduKarves.filter((k) => laktacijos.includes(k.nr));
  }

  searchText !== "" &&
    (banduKarves = banduKarves.filter((k) =>
      k.karNr.toLowerCase().includes(searchText.toLowerCase())
    ));

  // Make sure we only call setOriginalusKarviuSkaicius once when the list changes
  useEffect(() => {
    if (setOriginalusKarviuSkaicius && karviuList && karviuList.length >= 0) {
      setOriginalusKarviuSkaicius(karviuList.length);
    }
  }, [karviuList, setOriginalusKarviuSkaicius]);

  useEffect(() => {
    if (!dynamicSize || !containerRef.current) return;

    const updateSize = () => {
      if (containerRef.current) {
        const { offsetWidth, offsetHeight } = containerRef.current;
        setListDimensions({
          width: offsetWidth,
          height: offsetHeight - (stickyHeader && searchText === "" ? 25 : 0),
        });
      }
    };

    requestAnimationFrame(updateSize);

    const resizeObserver = new ResizeObserver(updateSize);
    resizeObserver.observe(containerRef.current);

    return () => {
      resizeObserver.disconnect();
    };
  }, [dynamicSize, karviuList.length, searchText]);

  const handleZymKarve = (target) => {
    changeKarves(target.checked, target.value);
    if (target.checked && searchText === "") {
      setZymVisos(karves.length === banduKarves.length);
    } else {
      setZymVisos(false);
    }
  };

  const handleZymVisas = (checked) => {
    if (checked) {
      setZymVisos(true);
      handleZymVisasKarves(banduKarves.map((karve) => karve.karNr));
    } else {
      setZymVisos(false);
      handleZymVisasKarves([]);
    }
  };

  const handleSearch = (value) => {
    setSearchText(value);
    setZymVisos(false);
    changeKarves([]);
  };

  const renderRow = (props) => {
    const { data, index, style } = props;
    if (data[index] === undefined) {
      return null;
    }
    const isChecked = karves.indexOf(data[index].karNr) > -1 || zymVisos;
    const handleClick = () => {
      handleZymKarve({ checked: !isChecked, value: data[index].karNr });
    };

    return (
      <ListItem style={style} key={index} component="div" disablePadding onClick={handleClick}>
        <ListItemButton>
          <ListItemIcon>
            <Checkbox checked={isChecked} value={data[index].karNr} />
          </ListItemIcon>
          <ListItemText primary={data[index].karNr} />
        </ListItemButton>
      </ListItem>
    );
  };

  if (isLoading) {
    return (
      <>
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: "16px",
            width: "100%",
            marginBottom: "4px",
          }}
        >
          <Skeleton width="50%" height={30} />
          {[...Array(5).keys()].map((s) => (
            <Box key={s} sx={{ display: "flex", gap: "20px", paddingLeft: "30px" }}>
              <Skeleton width={30} height={30} />
              <Skeleton width="100%" />
            </Box>
          ))}
        </Box>
      </>
    );
  }

  if (karviuList.length === 0) {
    return <Typography>Karvės</Typography>;
  }

  return (
    <>
      <KarvesContainer>
        <Typography>{karves ? "Karvės" : "Pasirinkite karves"}</Typography>
        <TextField
          id="karNr"
          name="karNr"
          type="text"
          value={searchText}
          size="small"
          onChange={(e) => handleSearch(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
            style: {
              borderRadius: "12px",
              height: "24px",
              paddingLeft: "10px",
            },
          }}
        />
      </KarvesContainer>
      {banduKarves.length === 0 ? (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: "100%",
          }}
        >
          {/* <Typography color="red">Karvių su tokiu numeriu nerasta</Typography> */}
          <Typography color="red">Karvių pagal kriterijus nerasta</Typography>
        </Box>
      ) : (
        <>
          {stickyHeader && searchText === "" && (
            <Box
              sx={{ width: "100%" }}
              height="40px"
              marginBottom={1}
              onClick={() => handleZymVisas(!zymVisos)}
            >
              <ListItem
                component="div"
                disablePadding
                sx={{
                  height: "100%",
                  minHeight: "unset", // Prevent default min-height
                }}
              >
                <ListItemButton
                  sx={{
                    height: "100%",
                    minHeight: "unset", // Prevent default min-height
                  }}
                >
                  <ListItemIcon>
                    <Checkbox checked={zymVisos} />
                  </ListItemIcon>
                  <ListItemText primary="Žymėti visas" />
                </ListItemButton>
              </ListItem>
            </Box>
          )}
          <ListContainerWrapper ref={containerRef} width={"100%"}>
            <FixedSizeList
              height={dynamicSize ? listDimensions.height : 250}
              width={dynamicSize ? listDimensions.width : 230}
              itemData={
                !stickyHeader && searchText === ""
                  ? [{ karNr: "Žymėti visas" }, ...banduKarves]
                  : banduKarves
              }
              itemSize={50}
              itemCount={banduKarves.length !== "undefined" ? banduKarves.length + 1 : 0}
              style={{ scrollbarWidth: "thin" }}
            >
              {renderRow}
            </FixedSizeList>
          </ListContainerWrapper>
        </>
      )}
    </>
  );
};

export default KarvesProduktyvumui;
